/**
 * StatisticDaily
 * --
 */
import React from 'react';
import { Descriptions, Table, Button, Checkbox } from 'antd';
import { Content, Title, Row, Col } from 'components';
import moment from 'moment';
import MessageAlert, { stringToMoneyFormat } from 'utils';
// import { downloadXlsx } from 'utils/XlsxDownloader';
import * as XLSX from 'xlsx/xlsx.mjs';
/* 
사용중인 필드
  date: 날짜
  totalAmount: Total(합계금액+환불금액+할인금액)
  completedTotalAmount: 합계금액(결제완료건)
  netSales: 순매출(합계금액 - 할인금액)
  paymentsCount: 결제건수(결제완료건)
  discountAmount: 할인금액
  couponAppliedCount: 쿠폰적용 주문수
  refundAmount: 환불금액
  refundCount: 환불건수
  totalAmountKIOSK: KIOSK합계금액
  paymentsCountKIOSK: KIOSK결제건수
  totalAmountAPP: APP합계금액
  paymentsCountAPP: APP결제건수
  totalAmountPOS: POS합계금액
  paymentsCountPOS: POS결제건수
*/

/**----------------------------------------
 * [Component] 일자별 매출
 * ----------------------------------------
 */
const StatisticDaily = ({
  // shopList, // 업체목록
  // currentShop, // 선택된 업체 키
  data, // 차트 데이터
  startDate,
  endDate,
  onSelect,
  dateRange,
  finalDiscount,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  /* ===== VARIABLE ===== */
  // const currentShopInfo = shopList.filter(({ key }) => key === currentShop)[0];
  /* ===== FUNCTION ===== */
  /**
   * 엑셀 다운로드
   * --
   */
  const handleDownloadExel = () => {
    try {
      const xlsxData = data.map((item, idx) => {
        return {
          ['시간대']: item.time_range,
          ['총매출']: Number(item.totalAmount),
          ['순매출']: Number(item.netsAmount),
          ['총 결제건수']: Number(item.paymentCount ?? 0),
          ['할인']: Number(item.discountAmount ?? 0),
          ['사용포인트']: Number(item.usePointAmount ?? 0),
          ['환불금액']: Number(item.refundAmount ?? 0),
          ['환불건수']: Number(item.refundCount ?? 0),
          ['KIOSK총매출']: Number(item.kioskAmount ?? 0),
          ['KIOSK결제건수']: Number(item.kioskCount ?? 0),
          ['APP총매출']: Number(item.appAmount ?? 0),
          ['APP결제건수']: Number(item.appCount ?? 0),
          ['POS총매출']: Number(item.posAmount ?? 0),
          ['POS결제건수']: Number(item.posCount ?? 0),
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(
        wb,
        `시간대별 매출_${moment().format('YYYYMMDDHHmmss')}.xlsx`
      );
    } catch (e) {
      console.log(e);
      MessageAlert.error(e.message);
    }
  };

  /**
   *
   * --
   */
  const handleCalculate = (d, key) => {
    return d.reduce((acc, cur) => acc + Number(cur[key] ?? 0), 0);
  };
  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */
  const col = [
    {
      title: '시간대',
      dataIndex: 'time_range',
      width: 145,
      fixed: 'left',
      filters: Array.from({ length: 24 }, (_, i) => {
        const hour = String(i).padStart(2, '0');
        const start = `${hour}:00`;
        const end = `${hour}:59`;
        const time_range = `${start}-${end}`;

        return {
          text: start,
          value: time_range,
        };
      }),
      onFilter: (value, record) => record?.time_range === value,
    },
    {
      title: '총매출',
      dataIndex: 'totalAmount',
      width: 120,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.totalAmount) - Number(b.totalAmount),
    },
    {
      title: (
        <span style={{ lineHeight: 0.5 }}>
          순매출
          <br />
          <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
            (총매출-할인)
          </span>
        </span>
      ),
      dataIndex: 'netsAmount',
      width: 128,
      render: (v, record) => {
        // console.log('#### ####', moment(record.date).format('YYYY-MM-DD(ddd)'));
        // console.log('totalAmount: ', v);
        // console.log('totalAmount: ', record.discountAmount);

        return (
          <div style={{ textAlign: 'right' }}>
            {stringToMoneyFormat(Number(v))}
          </div>
        );
      },
      sorter: (a, b) => Number(a.netsAmount) - Number(b.netsAmount),
    },
    {
      title: '총 결제건수',
      dataIndex: 'paymentCount',
      width: 100,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.paymentCount) - Number(b.paymentCount),
    },
    {
      title: '할인',
      dataIndex: 'discountAmount',
      width: 102,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.discountAmount) - Number(b.discountAmount),
    },
    {
      title: '사용포인트',
      dataIndex: 'usePointAmount',
      width: 102,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.usePointAmount) - Number(b.usePointAmount),
    },
    {
      // title: '쿠폰',
      title: '쿠폰적용 주문수',
      dataIndex: 'couponAppliedCount',
      width: 125,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) =>
        Number(a.couponAppliedCount) - Number(b.couponAppliedCount),
    },
    {
      title: '환불금액',
      dataIndex: 'refundAmount',
      width: 125,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.refundAmount) - Number(b.refundAmount),
    },
    {
      title: '환불건수',
      dataIndex: 'refundCount',
      width: 90,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.refundCount) - Number(b.refundCount),
    },
    {
      title: (
        <>
          KIOSK
          <br />
          총매출
        </>
      ),
      dataIndex: 'kioskAmount',
      width: 110,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.kioskAmount) - Number(b.kioskAmount),
    },
    {
      title: (
        <>
          KIOSK
          <br />
          결제건수
        </>
      ),
      dataIndex: 'kioskCount',
      width: 110,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.kioskCount) - Number(b.kioskCount),
    },
    {
      title: (
        <>
          APP
          <br />
          총매출
        </>
      ),
      dataIndex: 'appAmount',
      width: 110,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.appAmount) - Number(b.appAmount),
    },
    {
      title: (
        <>
          APP
          <br />
          결제건수
        </>
      ),
      dataIndex: 'appCount',
      width: 110,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.appCount) - Number(b.appCount),
    },
    {
      title: (
        <>
          POS
          <br />
          총매출
        </>
      ),
      dataIndex: 'posAmount',
      width: 110,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.posAmount) - Number(b.posAmount),
    },
    {
      title: (
        <>
          POS
          <br />
          결제건수
        </>
      ),
      dataIndex: 'posCount',
      width: 110,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.posCount) - Number(b.posCount),
    },
  ];

  const [columnsKey, setColumnsKey] = React.useState(
    col.map((c) => c.dataIndex)
  );

  const visibleColumns = React.useMemo(() => {
    return col.filter((c) => columnsKey.includes(c.dataIndex));
  }, [columnsKey]);

  const columnOptions = col.map((c) => {
    return {
      label:
        typeof c.title !== 'string'
          ? c?.title?.props.children
              .map((child) =>
                typeof child === 'string' ? child : child.props?.children || ''
              )
              .join(' ')
          : c.title,
      value: c.dataIndex,
    };
  });
  // console.log('[daily]data: ', data);
  // console.log('[daily]finalDiscount: ', Math.abs(Number(finalDiscount)));
  // console.log(
  //   '[daily]discountAmount: ',
  //   data ? Math.abs(handleCalculate(data, 'discountAmount')) : 0
  // );

  // const finalDiscount2 = Math.abs(Number(finalDiscount));
  // console.log('[DAILY] finalDiscount2: ', finalDiscount2);

  return (
    <>
      <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
        <Col
          x={24}
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            marginBottom: '5px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Checkbox
            indeterminate={
              columnsKey.length > 0 && columnsKey.length < columnOptions.length
            }
            checked={columnsKey.length === columnOptions.length}
            onChange={(e) => {
              if (e.target.checked) {
                setColumnsKey(columnOptions.map((c) => c.value));
              } else {
                setColumnsKey([]);
              }
            }}
          >
            전체 선택
          </Checkbox>
          <Checkbox.Group
            options={columnOptions}
            value={columnsKey}
            onChange={(checkedValues) => setColumnsKey(checkedValues)}
          />
        </Col>
        <Row>
          {/* === 상단 > 타이틀 === */}
          <Col x={12} style={{ display: 'flex', gap: 5 }}>
            <Title
              style={{ marginBottom: 7.5 }}
              subButton={
                <Button
                  style={{ marginLeft: 7.5 }}
                  onClick={() => handleDownloadExel()}
                  // onClick={() =>
                  //   downloadXlsx({
                  //     columns: col?.map((column) => {
                  //       if (typeof column.title !== 'string') {
                  //         column.title = column?.title?.props.children
                  //           .map((child) =>
                  //             typeof child === 'string'
                  //               ? child
                  //               : child.props?.children || ''
                  //           )
                  //           .join(' ');
                  //       }
                  //       return column;
                  //     }),
                  //     rows: data?.map((_item) => {
                  //       return Object.fromEntries(
                  //         Object.entries(_item).map(([key, value]) => [
                  //           key,
                  //           value ?? 0,
                  //         ])
                  //       );
                  //     }),
                  //     fileName: '일자별_리포트',
                  //   })
                  // }
                >
                  엑셀로 다운로드
                </Button>
              }
            >
              시간대별 리포트
            </Title>
          </Col>
          <Col x={12} style={{ textAlign: 'right' }}>
            <Title
              level={5}
              align="right"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginBottom: 7.5,
              }}
            >
              <small>조회기간:</small>&nbsp;&nbsp;
              {!dateRange.startDate || !dateRange.endDate ? (
                <>
                  {moment(startDate).format('YYYY.MM.DD(ddd)')}~
                  {moment(endDate).format('YYYY.MM.DD(ddd)')}
                </>
              ) : (
                <>
                  {moment(dateRange.startDate).format('YYYY.MM.DD(ddd)')}~
                  {moment(dateRange.endDate).format('YYYY.MM.DD(ddd)')}
                </>
              )}
            </Title>
          </Col>
        </Row>

        {/* ===== 데이터 Header: start ===== */}
        <div style={{ overflowX: 'auto', background: '#fff' }}>
          {data && (
            <Descriptions
              className="boxShadow"
              size="small"
              layout="vertical"
              column={15}
              bordered
              style={{ width: 1710 }}
              contentStyle={{ textAlign: 'right' }}
              items={[
                {
                  key: 'date',
                  label: '',
                  style: { width: 145 },
                  contentStyle: { textAlign: 'center' },
                  children: <p>합계</p>,
                },
                {
                  key: 'completedTotalAmount',
                  label: '총매출',
                  style: { width: 120 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'totalAmount') ??
                          0 + handleCalculate(data, 'refundAmount') ??
                          0
                      )}
                    </p>
                  ),
                },
                {
                  key: 'netSales',
                  label: (
                    <span style={{ lineHeight: 0.5 }}>
                      순매출
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
                        (총매출-할인)
                      </span>
                    </span>
                  ),
                  style: { width: 130 },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'netsAmount'))}
                    </p>
                  ),
                },
                {
                  key: 'paymentsCount',
                  label: '총 결제건수',
                  style: { width: 100 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'paymentCount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'discountAmount',
                  label: '할인',
                  style: { width: 100 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'discountAmount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'usePointAmount',
                  label: '사용포인트',
                  style: { width: 100 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'usePointAmount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'couponAppliedCount',
                  // label: '쿠폰',
                  label: '쿠폰적용 주문',
                  style: { width: 123 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'couponAppliedCount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'refundAmount',
                  label: '환불금액',
                  style: { width: 125 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'refundAmount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'refundCount',
                  label: '환불건수',
                  style: { width: 90 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'refundCount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'kioskAmount',
                  label: (
                    <>
                      KIOSK
                      <br />
                      총매출
                    </>
                  ),
                  style: { width: 110 },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'kioskAmount')
                      )}
                    </p>
                  ),
                },
                {
                  key: 'kioskCount',
                  label: (
                    <>
                      KIOSK
                      <br />
                      결제건수
                    </>
                  ),
                  style: { width: 110 },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'kioskCount'))}
                    </p>
                  ),
                },
                {
                  key: 'appAmount',
                  label: (
                    <>
                      APP
                      <br />
                      총매출
                    </>
                  ),
                  style: { width: 110 },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'appAmount'))}
                    </p>
                  ),
                },
                {
                  key: 'appCount',
                  label: (
                    <>
                      APP
                      <br />
                      결제건수
                    </>
                  ),
                  style: { width: 110 },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'appCount'))}
                    </p>
                  ),
                },
                {
                  key: 'posAmount',
                  label: (
                    <>
                      POS
                      <br />
                      총매출
                    </>
                  ),
                  style: { width: 110 },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'posAmount'))}
                    </p>
                  ),
                },
                {
                  key: 'posCount',
                  label: (
                    <>
                      POS
                      <br />
                      결제건수
                    </>
                  ),
                  style: { width: 110 },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'posCount'))}
                    </p>
                  ),
                },
              ]}
            />
          )}
        </div>
        {/* ===== 데이터 Header: end ===== */}
        <br />

        {/* ===== 데이터 테이블: start ===== */}
        <div style={{ overflow: 'auto', width: '100%', height: '100%' }}>
          <Table
            className="boxShadow"
            bordered
            size="small"
            dataSource={data ?? []}
            scroll={{
              x: 'max-content',
            }}
            style={{ minHeight: 580 }}
            // onRow={(record) => {
            //   return {
            //     onClick: () => onSelect(record), // 행 클릭 시 이벤트 처리
            //   };
            // }}
            columns={visibleColumns}
          />
        </div>
        {/* ===== 데이터 테이블: end ===== */}
      </Content>
    </>
  );
};

StatisticDaily.defaultProps = {
  onSelect: () => {},
};
export default StatisticDaily;
